import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Mail, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button, Spinner, ConfirmationModal, Checkbox } from '@crc-evans/ui-components';
import { message, Input, Tooltip } from 'antd';
import {
  fetchReportList,
  addReport,
  updateReport,
  deleteReport,
} from '../../redux/reportEmail/action';
import * as constants from '../../utils/constants';
import * as utility from '../../utils/utility';
import './DPRSettingsReportingEmailTab.css';
import '../../pages/technical/HeatProfilePage.css';
import './DPRSettingsPipelinesTab.css';
import { ProjectTabTableEmpty } from '../project/ProjectTabTableEmpty';

/** Min px width for uppercase report header from label length. */
const reportHeaderColWidth = (label, floor = 64) =>
  Math.max(floor, Math.ceil(label.length * 9) + 32);

const REPORT_TYPE_KEYS = [
  { key: 'dsrReport', label: 'DSR' },
  { key: 'ppmReport', label: 'PPM' },
  { key: 'ppmIssuesReport', label: 'PPM Issues' },
  { key: 'communicationsReport', label: 'Communications' },
  { key: 'builtToStandardReport', label: 'Build to Standard' },
  { key: 'servicetoStandardReport', label: 'Service to Standard' },
  { key: 'qcReport', label: 'QC' },
  { key: 'sparePartsOrder', label: 'Spare Parts Order' },
  { key: 'safetyStock', label: 'Safety Stock' },
  { key: 'dprReport', label: 'DPR' },
  { key: 'shipmentReport', label: 'Shipment' },
  { key: 'materialConsumptionReport', label: 'Material Expiry' },
  { key: 'consumablesOrder', label: 'Consumables Order' },
  { key: 'spaceLogReport', label: 'Space Log' },
].map(({ key, label }) => ({ key, label, colWidth: reportHeaderColWidth(label) }));

const REPORTING_EMAIL_TABLE_MIN_WIDTH =
  40 +
  250 +
  REPORT_TYPE_KEYS.reduce((sum, { colWidth }) => sum + colWidth, 0) +
  100 +
  REPORT_TYPE_KEYS.length * 8 +
  24;

const REPORT_MAPPING = {
  dsrReport: '1',
  ppmReport: '2',
  ppmIssuesReport: '3',
  communicationsReport: '4',
  builtToStandardReport: '5',
  servicetoStandardReport: '6',
  sparePartsOrder: '7',
  qcReport: '8',
  shipmentReport: '9',
  dprReport: '10',
  safetyStock: '12',
  materialConsumptionReport: '14',
  consumablesOrder: '15',
  spaceLogReport: constants.REPORT_EMAIL_SPACE_LOG_ID,
};

function processReportDestinationEmails(reportDestinationEmails) {
  if (!reportDestinationEmails) return [];
  const emailReportMap = {};
  Object.keys(reportDestinationEmails).forEach((reportType) => {
    reportDestinationEmails[reportType].forEach((email) => {
      if (!emailReportMap[email]) {
        emailReportMap[email] = {
          email,
          dsrReport: '',
          ppmReport: '',
          ppmIssuesReport: '',
          communicationsReport: '',
          builtToStandardReport: '',
          servicetoStandardReport: '',
          qcReport: '',
          sparePartsOrder: '',
          safetyStock: '',
          dprReport: '',
          shipmentReport: '',
          materialConsumptionReport: '',
          consumablesOrder: '',
          spaceLogReport: '',
        };
      }
      const keyMap = {
        '1': 'dsrReport',
        '2': 'ppmReport',
        '3': 'ppmIssuesReport',
        '4': 'communicationsReport',
        '5': 'builtToStandardReport',
        '6': 'servicetoStandardReport',
        '7': 'sparePartsOrder',
        '8': 'qcReport',
        '9': 'shipmentReport',
        '10': 'dprReport',
        '12': 'safetyStock',
        '14': 'materialConsumptionReport',
        '15': 'consumablesOrder',
        [constants.REPORT_EMAIL_SPACE_LOG_ID]: 'spaceLogReport',
      };
      if (keyMap[reportType]) emailReportMap[email][keyMap[reportType]] = keyMap[reportType];
    });
  });
  return Object.values(emailReportMap);
}

function addEmailToReportDestinationEmails(userList, keys, email) {
  userList.forEach((user) => {
    if (!user.config) user.config = {};
    if (!user.config.reportDestinationEmails) user.config.reportDestinationEmails = {};
    const reportDestinationEmails = user.config.reportDestinationEmails;
    keys.forEach((key) => {
      if (reportDestinationEmails[key]) {
        if (!reportDestinationEmails[key].includes(email)) reportDestinationEmails[key].push(email);
      } else {
        reportDestinationEmails[key] = [email];
      }
    });
  });
  return userList;
}

function updateEmailInReportDestinationEmails(userList, keys, oldEmail, newEmail) {
  userList.forEach((user) => {
    if (!user.config?.reportDestinationEmails) user.config = { reportDestinationEmails: {} };
    const reportDestinationEmails = user.config.reportDestinationEmails;
    Object.keys(reportDestinationEmails).forEach((key) => {
      const emailList = reportDestinationEmails[key];
      const idx = emailList.indexOf(oldEmail);
      if (keys.includes(key)) {
        if (idx !== -1) emailList[idx] = newEmail;
        else if (!emailList.includes(newEmail)) emailList.push(newEmail);
      } else if (idx !== -1) emailList.splice(idx, 1);
    });
    keys.forEach((key) => {
      if (!reportDestinationEmails[key]) reportDestinationEmails[key] = [newEmail];
      else if (!reportDestinationEmails[key].includes(newEmail)) reportDestinationEmails[key].push(newEmail);
    });
  });
  return userList;
}

function removeEmailFromReportDestinationEmails(userList, keys, email) {
  userList.forEach((user) => {
    const reportDestinationEmails = user.config?.reportDestinationEmails;
    if (!reportDestinationEmails) return;
    keys.forEach((key) => {
      if (reportDestinationEmails[key]) {
        const idx = reportDestinationEmails[key].indexOf(email);
        if (idx !== -1) {
          reportDestinationEmails[key].splice(idx, 1);
          if (reportDestinationEmails[key].length === 0) delete reportDestinationEmails[key];
        }
      }
    });
  });
  return userList;
}

const ALL_REPORT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '12', '14', '15', constants.REPORT_EMAIL_SPACE_LOG_ID];

const DPRSettingsReportingEmailTab = ({
  selectedProject,
  usersList,
  isUserListLoading,
  onFetchReportList,
  onAddReport,
  onUpdateReport,
  onDeleteReport,
  projectTeamMembersAccessList,
  userAccessList,
}) => {
  const [reportEmails, setReportEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [modalEmail, setModalEmail] = useState('');
  const [modalReportAccess, setModalReportAccess] = useState({});

  const isWriteAccess = (() => {
    const groups =
      userAccessList?.length > 0
        ? userAccessList
        : (() => {
          try {
            return JSON.parse(localStorage.getItem('UserGroup') || '[]');
          } catch (e) {
            return [];
          }
        })();
    if (Array.isArray(groups) && (groups.includes('admin') || groups.includes('super-admin'))) return true;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return false;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !projectTeamMembersAccessList?.[0]?.tabPermissions?.['Reporting Email']?.write
    )
      return false;
    return true;
  })();

  useEffect(() => {
    if (selectedProject?.id) onFetchReportList(selectedProject);
  }, [selectedProject?.id, onFetchReportList]);

  useEffect(() => {
    const list = Array.isArray(usersList) ? usersList : usersList ? [usersList] : [];
    if (!list.length) {
      setReportEmails([]);
      setFilteredEmails([]);
      return;
    }
    const user = list[0];
    const reportDestinationEmails = user?.config?.reportDestinationEmails;
    const processed = processReportDestinationEmails(reportDestinationEmails);
    setReportEmails(processed);
    setFilteredEmails(processed);
  }, [usersList, selectedProject]);

  useEffect(() => {
    if (!searchKey.trim()) {
      setFilteredEmails(reportEmails);
      return;
    }
    const lower = searchKey.toLowerCase();
    setFilteredEmails(reportEmails.filter((row) => row.email?.toLowerCase().includes(lower)));
  }, [searchKey, reportEmails]);

  const openAddModal = () => {
    if (!selectedProject) {
      message.warning('Select a project to add email.');
      return;
    }
    setModalEmail('');
    setModalReportAccess(REPORT_TYPE_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: false }), {}));
    setIsAddModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    setModalEmail(record.email || '');
    setModalReportAccess(
      REPORT_TYPE_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: !!record[key] }), {})
    );
    setIsEditModalOpen(true);
  };

  const closeAddModal = () => setIsAddModalOpen(false);
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditRecord(null);
  };

  const handleAddSubmit = () => {
    const email = (modalEmail || '').trim();
    if (!email) {
      message.warning('Please enter an email.');
      return;
    }
    if (!constants.EMAIL_PATTERN.test(email)) {
      message.warning('Please enter a valid email.');
      return;
    }
    const keys = REPORT_TYPE_KEYS.filter(({ key }) => modalReportAccess[key]).map(
      ({ key }) => REPORT_MAPPING[key]
    );
    if (keys.length === 0) {
      message.warning('Select at least one report type.');
      return;
    }
    const trimmedEmail = (modalEmail && utility.trimSpacesInValues(modalEmail)) || email;
    const obj = addEmailToReportDestinationEmails(usersListArray, keys, trimmedEmail);
    onAddReport({ data: obj[0], selectedProject });
    closeAddModal();
  };

  const handleEditSubmit = () => {
    const email = (modalEmail || '').trim();
    if (!email || !editRecord) return;
    if (!constants.EMAIL_PATTERN.test(email)) {
      message.warning('Please enter a valid email.');
      return;
    }
    const keys = REPORT_TYPE_KEYS.filter(({ key }) => modalReportAccess[key]).map(
      ({ key }) => REPORT_MAPPING[key]
    );
    if (keys.length === 0) {
      message.warning('Select at least one report type.');
      return;
    }
    const trimmedEmail = (modalEmail && utility.trimSpacesInValues(modalEmail)) || email;
    const obj = updateEmailInReportDestinationEmails(
      usersListArray,
      keys,
      editRecord.email,
      trimmedEmail
    );
    onUpdateReport({ data: obj[0], selectedProject });
    closeEditModal();
  };

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!recordToDelete?.email) return;
    const obj = removeEmailFromReportDestinationEmails(
      usersListArray,
      ALL_REPORT_KEYS,
      recordToDelete.email
    );
    onDeleteReport({ data: obj[0], selectedProject });
    setIsDeleteModalOpen(false);
    setRecordToDelete(null);
  };

  const toggleReportAccess = (key, checked) => {
    setModalReportAccess((prev) => ({ ...prev, [key]: !!checked }));
  };

  const isAllReportAccessSelected =
    REPORT_TYPE_KEYS.length > 0 &&
    REPORT_TYPE_KEYS.every(({ key }) => modalReportAccess[key]);

  const handleSelectAllReportAccess = (checked) => {
    setModalReportAccess(
      REPORT_TYPE_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: !!checked }), {})
    );
  };

  const list = filteredEmails;
  const usersListArray = Array.isArray(usersList) ? usersList : usersList ? [usersList] : [];

  return (
    <div className="dpr-settings-reporting-email-tab">
      {!selectedProject ? (
        <div className="dpr-settings-reporting-email-card">
          <p style={{ margin: 0, color: '#64748B' }}>Select a project to manage reporting emails.</p>
        </div>
      ) : (
        <>
          <div className="dpr-settings-reporting-email-card">
            <div className="dpr-settings-reporting-email-header">
              <div className="dpr-settings-reporting-email-header-left">
                <div className="dpr-settings-reporting-email-icon-wrap">
                  <Mail size={24} />
                </div>
                <div className="dpr-settings-reporting-email-header-content">
                  <div className="dpr-settings-reporting-email-title-row">
                    <h3 className="dpr-settings-reporting-email-title">Reporting Email</h3>
                    <span className="dpr-settings-reporting-email-summary">
                      Total: {list.length} {list.length === 1 ? 'email' : 'emails'}
                    </span>
                  </div>
                  <p className="dpr-settings-reporting-email-description">
                    Manage report destination emails and which reports each recipient receives.
                  </p>
                </div>
              </div>
              <div className="dpr-settings-reporting-email-actions">
                <Button
                  label="Add Email"
                  variant="primary"
                  size="small"
                  icon={Plus}
                  onClick={openAddModal}
                  disabled={!isWriteAccess}
                />
              </div>
            </div>

            <div
              className="dpr-settings-reporting-email-table-wrap"
              style={{ '--reporting-email-table-min-width': `${REPORTING_EMAIL_TABLE_MIN_WIDTH}px` }}
            >
              <div className="dpr-settings-reporting-email-thead">
                <div className="dpr-settings-reporting-email-header-row dpr-settings-reporting-email-header-row--heat">
                  <div className="dpr-settings-reporting-email-cell dpr-settings-reporting-email-cell-num dpr-settings-reporting-email-cell-header dpr-settings-reporting-email-cell-header-compact">
                    <div className="column-header-with-filter column-header-with-filter--no-search">
                      <div className="column-header-text-wrapper dpr-pipelines-heat-title-row">
                        <span className="dpr-pipelines-heat-label">#</span>
                      </div>
                    </div>
                  </div>
                  <div className="dpr-settings-reporting-email-cell dpr-settings-reporting-email-cell-email dpr-settings-reporting-email-cell-header">
                    <div className="column-header-with-filter">
                      <div className="column-header-text-wrapper dpr-pipelines-heat-title-row dpr-pipelines-heat-title-row--left">
                        <span className="dpr-pipelines-heat-label">EMAIL</span>
                      </div>
                      <div className="header-filter-input user-mgmt-header-search dpr-settings-reporting-email-header-search">
                        <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
                          <Input
                            placeholder="Search"
                            value={searchKey}
                            onChange={(e) => setSearchKey(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                            aria-label="Search by email"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {REPORT_TYPE_KEYS.map(({ key, label, colWidth }) => (
                    <div
                      key={key}
                      className="dpr-settings-reporting-email-cell dpr-settings-reporting-email-cell-check dpr-settings-reporting-email-cell-header dpr-settings-reporting-email-cell-header-compact"
                      style={{ width: colWidth, minWidth: colWidth }}
                    >
                      <div className="column-header-with-filter column-header-with-filter--no-search">
                        <div className="column-header-text-wrapper dpr-pipelines-heat-title-row dpr-settings-reporting-email-report-title-row">
                          <Tooltip title={label} placement="top">
                            <span className="dpr-pipelines-heat-label">{label.toUpperCase()}</span>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="dpr-settings-reporting-email-cell dpr-settings-reporting-email-cell-actions dpr-settings-reporting-email-cell-header dpr-settings-reporting-email-cell-header-compact">
                    <div className="column-header-with-filter column-header-with-filter--no-search">
                      <div className="column-header-text-wrapper dpr-pipelines-heat-title-row">
                        <span className="dpr-pipelines-heat-label">ACTIONS</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isUserListLoading ? (
                <div className="dpr-settings-reporting-email-loading">
                  <Spinner size="medium" />
                </div>
              ) : list.length === 0 ? (
                <div className="dpr-settings-reporting-email-empty">
                  <ProjectTabTableEmpty />
                </div>
              ) : (
                <div className="dpr-settings-reporting-email-rows">
                  {list.map((row, index) => (
                    <div key={`${row.email}-${index}`} className="dpr-settings-reporting-email-row">
                      <div className="dpr-settings-reporting-email-cell dpr-settings-reporting-email-cell-num">
                        {index + 1}
                      </div>
                      <div className="dpr-settings-reporting-email-cell dpr-settings-reporting-email-cell-email">
                        {row.email || '—'}
                      </div>
                      {REPORT_TYPE_KEYS.map(({ key, colWidth }) => (
                        <div
                          key={key}
                          className="dpr-settings-reporting-email-cell dpr-settings-reporting-email-cell-check"
                          style={{ width: colWidth, minWidth: colWidth }}
                        >
                          <Checkbox
                            checked={!!row[key]}
                            disabled
                            borderColor="#00C9A7"
                            onChange={() => { }}
                          />
                        </div>
                      ))}
                      <div className="dpr-settings-reporting-email-cell dpr-settings-reporting-email-cell-actions">
                        <button
                          type="button"
                          className="dpr-settings-reporting-email-action-btn"
                          onClick={() => openEditModal(row)}
                          disabled={!isWriteAccess}
                          title="Update"
                          aria-label="Update"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          type="button"
                          className="dpr-settings-reporting-email-action-btn dpr-settings-reporting-email-action-delete"
                          onClick={() => handleDeleteClick(row)}
                          disabled={!isWriteAccess}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Add Email Modal */}
      {isAddModalOpen && (
        <div className="dpr-settings-reporting-email-modal-backdrop" onClick={closeAddModal}>
          <div
            className="dpr-settings-reporting-email-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dpr-settings-reporting-email-modal-head">
              <h3 className="dpr-settings-reporting-email-modal-title">Add Email</h3>
              <button
                type="button"
                className="dpr-settings-reporting-email-modal-close"
                onClick={closeAddModal}
                aria-label="Close"
              >
                <span>×</span>
              </button>
            </div>
            <div className="dpr-settings-reporting-email-modal-body">
              <div className="dpr-settings-reporting-email-modal-field">
                <label>Email</label>
                <input
                  type="text"
                  placeholder="Enter email"
                  value={modalEmail}
                  onChange={(e) => setModalEmail(e.target.value)}
                  className="dpr-settings-reporting-email-modal-input"
                />
              </div>
              <div className="dpr-settings-reporting-email-modal-field">
                <label>Report Email Access</label>
                <div className="dpr-settings-reporting-email-select-all-row">
                  <Checkbox
                    label="Select ALL"
                    checked={isAllReportAccessSelected}
                    onChange={(checked) => handleSelectAllReportAccess(!!checked)}
                    borderColor="#00C9A7"
                  />
                </div>
                <div className="dpr-settings-reporting-email-checkbox-grid">
                  {REPORT_TYPE_KEYS.map(({ key, label }) => (
                    <Checkbox
                      key={key}
                      label={label}
                      checked={!!modalReportAccess[key]}
                      onChange={(checked) => toggleReportAccess(key, checked)}
                      borderColor="#00C9A7"
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="dpr-settings-reporting-email-modal-foot">
              <Button label="Cancel" variant="tertiary" onClick={closeAddModal} size="medium" />
              <Button
                label={isUserListLoading ? 'Adding…' : 'Add'}
                variant="primary"
                onClick={handleAddSubmit}
                disabled={isUserListLoading}
                size="medium"
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Email Modal */}
      {isEditModalOpen && (
        <div className="dpr-settings-reporting-email-modal-backdrop" onClick={closeEditModal}>
          <div
            className="dpr-settings-reporting-email-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dpr-settings-reporting-email-modal-head">
              <h3 className="dpr-settings-reporting-email-modal-title">Update Email</h3>
              <button
                type="button"
                className="dpr-settings-reporting-email-modal-close"
                onClick={closeEditModal}
                aria-label="Close"
              >
                <span>×</span>
              </button>
            </div>
            <div className="dpr-settings-reporting-email-modal-body">
              <div className="dpr-settings-reporting-email-modal-field">
                <label>Email</label>
                <input
                  type="text"
                  placeholder="Enter email"
                  value={modalEmail}
                  onChange={(e) => setModalEmail(e.target.value)}
                  className="dpr-settings-reporting-email-modal-input"
                />
              </div>
              <div className="dpr-settings-reporting-email-modal-field">
                <label>Report Email Access</label>
                <div className="dpr-settings-reporting-email-select-all-row">
                  <Checkbox
                    label="Select ALL"
                    checked={isAllReportAccessSelected}
                    onChange={(checked) => handleSelectAllReportAccess(!!checked)}
                    borderColor="#00C9A7"
                  />
                </div>
                <div className="dpr-settings-reporting-email-checkbox-grid">
                  {REPORT_TYPE_KEYS.map(({ key, label }) => (
                    <Checkbox
                      key={key}
                      label={label}
                      checked={!!modalReportAccess[key]}
                      onChange={(checked) => toggleReportAccess(key, checked)}
                      borderColor="#00C9A7"
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="dpr-settings-reporting-email-modal-foot">
              <Button label="Cancel" variant="tertiary" onClick={closeEditModal} size="medium" />
              <Button
                label={isUserListLoading ? 'Updating…' : 'Update'}
                variant="primary"
                onClick={handleEditSubmit}
                disabled={isUserListLoading}
                size="medium"
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        title="Delete Email"
        message={`Are you sure you want to delete ${recordToDelete?.email || 'this email'}?`}
        confirmText={isUserListLoading ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        size="medium"
        confirmButtonProps={{
          style: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
          disabled: isUserListLoading,
        }}
      />
    </div>
  );
};

const mapStateToProps = ({ equip, reportEmailReducer }) => ({
  usersList: get(reportEmailReducer, 'reportEmailData', []),
  isUserListLoading: get(reportEmailReducer, 'isFetchReportEmailDataLoading', false),
  projectTeamMembersAccessList: get(equip, 'projectTeamMembersAccessList', []),
  userAccessList: get(equip, 'userAccessList', []),
});

const mapDispatchToProps = {
  onFetchReportList: fetchReportList,
  onAddReport: addReport,
  onUpdateReport: updateReport,
  onDeleteReport: deleteReport,
};

export default connect(mapStateToProps, mapDispatchToProps)(DPRSettingsReportingEmailTab);
